/**
 * Grader — the heart of Sports Director.
 *
 * Consumes one GradeTickInput per sim tick (30Hz) and keeps:
 *  - a live 0..100 score per category (exponentially weighted: each decision
 *    nudges the score toward a target, continuous signals drift toward one),
 *  - a points total with a rhythm-game streak multiplier (x1..x4),
 *  - the key-moment list (importance >= 0.6: covered? replayed?),
 *  - rate-limited feedback toasts (big moments always get through).
 *
 * Fairness rules: reaction grace after events (difficulty-scaled), holding
 * MAIN WIDE in open play is never punished, replays only cost coverage when
 * live play is dangerous, and replay-expiry only penalises when there was
 * actually a stoppage to roll it in. Only `open_play` is ball-in-play: kickoff
 * walk-backs and set-piece line-ups are dead ball (replay / reaction time) and
 * restarts are judged when the ball goes live. Reaction close-ups are never
 * owed while the ball is in play. Calm cutaways barely dent coverage; leaving
 * an attack does. Tuned against real matches with `npm run balance`.
 */
import type {
  CameraFrame, CommentatorRequest, DirectorState, GradeCategory, GradeFeedback,
  GradeReport, GradeTickInput, Grader, GraphicKind, MatchConfig, MatchEvent,
  MatchEventType, MatchPhase, WorldSnapshot,
} from '../core/contracts';
import { createRng } from '../core/rng';
import { type CamKindOf, inferCamKind, isCloseSize, isWideSize, NON_GAME_KINDS } from './cameras';
import { baseAudienceFor, createViewerModel } from './viewers';

export type Difficulty = 'rookie' | 'pro' | 'legend';
export type BoothStatus = 'hit' | 'late' | 'missed';

export interface GraderOptions {
  difficulty?: Difficulty;
  /** Map camId -> CameraKind. Defaults to the football kit naming ("CAM 1".., "BENCH", "CROWD"). */
  camKindOf?: CamKindOf;
  /** Override the base audience (otherwise derived from config.competition). */
  baseAudience?: number;
  /** Called when a commentator request resolves (wire to booth.resolve). */
  onBoothResolved?: (id: number, status: BoothStatus) => void;
  /** Balancing hook (scripts/balance.ts --diag): every discrete category decision. */
  onDecision?: (category: GradeCategory, target: number, weight: number, reason: string) => void;
}

export interface SportsGrader extends Grader {
  /** Current streak multiplier (1..4). */
  multiplier(): number;
  /** Resolution of a commentator request, once known. */
  requestStatus(id: number): BoothStatus | undefined;
  /** Peak viewers so far. */
  peakViewers(): number;
}

// ---------------------------------------------------------------------------
// Public constants
// ---------------------------------------------------------------------------

export const CATEGORY_WEIGHTS: Readonly<Record<GradeCategory, number>> = {
  coverage: 25, shot_choice: 15, pacing: 12, continuity: 8, replays: 15, graphics: 12, booth: 13,
};

export const LETTER_THRESHOLDS = [
  ['S', 92], ['A', 82], ['B', 70], ['C', 58], ['D', 45],
] as const;

export const GRACE_BY_DIFFICULTY: Readonly<Record<Difficulty, number>> = { rookie: 0.6, pro: 0.4, legend: 0.25 };
/** Penalty nudges are scaled by difficulty (rookie forgives a little more). */
const PENALTY_SCALE: Readonly<Record<Difficulty, number>> = { rookie: 0.8, pro: 1, legend: 1.2 };

export const START_SCORE = 70;
export const KEY_IMPORTANCE = 0.6;
export const REPLAY_WINDOW_S = 25;
export const BOOTH_LATE_WINDOW_S = 6;

export function letterFor(rating: number): GradeReport['letter'] {
  for (const [l, min] of LETTER_THRESHOLDS) if (rating >= min) return l;
  return 'F';
}

/** Categories below this drag the composite down extra ("weak link"). */
export const WEAK_LINK_FLOOR = 50;
export const WEAK_LINK_PENALTY = 0.4;

/**
 * Weighted mean of the categories, minus a weak-link penalty: ignoring a whole part of the
 * job (never answering the booth, never rolling a replay) costs more than its weight alone,
 * so "safe but dull" can't coast to a C on coverage.
 */
export function compositeRating(byCategory: Record<GradeCategory, number>): number {
  let sum = 0, w = 0, short = 0;
  for (const k of Object.keys(CATEGORY_WEIGHTS) as GradeCategory[]) {
    sum += byCategory[k] * CATEGORY_WEIGHTS[k];
    short += Math.max(0, WEAK_LINK_FLOOR - byCategory[k]) * CATEGORY_WEIGHTS[k];
    w += CATEGORY_WEIGHTS[k];
  }
  return Math.max(0, (sum - WEAK_LINK_PENALTY * short) / w);
}

/**
 * Difficulty scales the shortfall from a perfect 100: rookie forgives ~12% of what you
 * dropped, legend charges 10% extra. (On top of reaction grace, penalty scaling and a
 * busier booth.) Net effect across the skill range ≈ +6 rookie / −6 legend.
 */
export const DIFFICULTY_CURVE: Readonly<Record<Difficulty, number>> = { rookie: 0.88, pro: 1, legend: 1.1 };

export function applyDifficulty(composite: number, difficulty: Difficulty): number {
  return Math.max(0, Math.min(100, 100 - (100 - composite) * DIFFICULTY_CURVE[difficulty]));
}

/** x1 (0–2), x2 (3–7), x3 (8–15), x4 (16+). */
export function streakMultiplier(streak: number): number {
  return streak >= 16 ? 4 : streak >= 8 ? 3 : streak >= 3 ? 2 : 1;
}

const BASE_POINTS = { great: 150, good: 60, info: 10, bad: -50, terrible: -200 } as const;

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

/**
 * Phase model. The ball is only in play during `open_play`; `kickoff` and
 * `set_piece` are dead-ball phases (players lining up, the taker walking to
 * the ball) — but a set piece with intensity > DANGER (corner, penalty, direct
 * free kick about to be taken) is treated as live for coverage.
 */
const DANGER = 0.5;
const DEAD_AIR_PHASES: ReadonlySet<MatchPhase> = new Set<MatchPhase>(['stoppage', 'goal_celebration']);
const isLive = (s: WorldSnapshot): boolean =>
  s.phase === 'open_play' || ((s.phase === 'set_piece' || s.phase === 'kickoff') && s.intensity > DANGER);
/** Events where a reaction close-up is the right call. */
const REACTION_TYPES: ReadonlySet<MatchEventType> = new Set<MatchEventType>([
  'celebration', 'yellow_card', 'red_card', 'injury', 'manager_reaction', 'substitution',
]);
/** Reaction moments that aren't "action" — nothing to MISS on the wide shot. */
const NOT_KEY: ReadonlySet<MatchEventType> = new Set<MatchEventType>([
  'kickoff', 'half_time', 'full_time', 'celebration', 'manager_reaction', 'substitution', 'injury', 'crowd_moment',
]);
/** Nice-to-have reaction shots: a good cut is rewarded, staying on the action is never punished. */
const OPTIONAL_REACTIONS: ReadonlySet<MatchEventType> = new Set<MatchEventType>(['manager_reaction', 'crowd_moment']);
/** Same (non-big) toast label isn't shown again within this many seconds (points still count). */
const TOAST_REPEAT_S = 5;
/** A GREAT CUT has to hold the ideal shot this long (a flash-cut that lands it by luck doesn't count). */
const GREAT_CUT_HOLD_S = 1.2;
const RESTART_HOLD_S = 0.8;
const PASSIVE_GRAPHICS: ReadonlySet<GraphicKind> = new Set<GraphicKind>(['scorebug', 'clock', 'replay_bug']);

interface KeyCheck { ev: MatchEvent; deadline: number; covered: boolean }
interface ShotWindow {
  ev: MatchEvent; start: number; end: number; judged: number; matched: number;
  wantsTight: boolean; greatCutGiven: boolean;
  /** Reward-only window (nice-to-have reaction shot). */
  optional: boolean;
  /** Seconds the ideal shot has been held continuously. */
  matchRun: number;
}
interface ReplayPending { ev: MatchEvent; stoppageTime: number; replayed: boolean; resolved: boolean }
interface GfxPending { ev: MatchEvent; kind: GraphicKind; deadline: number }
interface Restart { t0: number; deadline: number; wasOffWide: boolean; held: number }
interface Queued { fb: GradeFeedback; at: number }
interface BoothTrack { req: CommentatorRequest; status?: BoothStatus; lateUntil: number }

interface PushOpts {
  eventId?: number;
  /** Bypasses the toast rate limit. Defaults to true for great/terrible. */
  big?: boolean;
  /** Whether this decision affects the streak. Default true. */
  streak?: boolean;
  /** Score-only: not toasted, not logged, no points, no streak. */
  silent?: boolean;
  /** Override base points. */
  points?: number;
}

const GFX_DEADLINES: Partial<Record<GraphicKind, number>> = {
  goal_lower_third: 15, card_lower_third: 15, sub_lower_third: 20, half_time_score: 12,
};
const GFX_OK_LABEL: Partial<Record<GraphicKind, string>> = {
  goal_lower_third: 'SCORER ✓', card_lower_third: 'CARD GFX ✓', sub_lower_third: 'SUB GFX ✓', half_time_score: 'HT SCORE ✓',
};
const GFX_MISS_LABEL: Partial<Record<GraphicKind, string>> = {
  goal_lower_third: 'NO SCORER GFX', card_lower_third: 'NO CARD GFX', sub_lower_third: 'NO SUB GFX', half_time_score: 'NO HT SCORE',
};

function defaultGraphicsFor(ev: MatchEvent): GraphicKind[] {
  if (ev.ideal.graphics) return ev.ideal.graphics.filter((g) => !PASSIVE_GRAPHICS.has(g));
  switch (ev.type) {
    case 'goal': return ['goal_lower_third'];
    case 'yellow_card': case 'red_card': return ['card_lower_third'];
    case 'substitution': return ['sub_lower_third'];
    default: return [];
  }
}

function keyLabel(ev: MatchEvent): string {
  return ev.text || ev.type.replace(/_/g, ' ').toUpperCase();
}

// ---------------------------------------------------------------------------
// Grader
// ---------------------------------------------------------------------------

export function createGrader(config: MatchConfig, opts: GraderOptions = {}): SportsGrader {
  const difficulty: Difficulty = opts.difficulty ?? 'pro';
  const grace = GRACE_BY_DIFFICULTY[difficulty];
  const penaltyScale = PENALTY_SCALE[difficulty];
  const camKindOf: CamKindOf = opts.camKindOf ?? inferCamKind;
  const viewers = createViewerModel(opts.baseAudience ?? baseAudienceFor(config.competition), createRng((config.seed ^ 0x51ed) >>> 0));

  const scores: Record<GradeCategory, number> = {
    coverage: START_SCORE, shot_choice: START_SCORE, pacing: START_SCORE, continuity: START_SCORE,
    replays: START_SCORE, graphics: START_SCORE, booth: START_SCORE,
  };
  const decisions: Record<GradeCategory, number> = {
    coverage: 0, shot_choice: 0, pacing: 0, continuity: 0, replays: 0, graphics: 0, booth: 0,
  };
  let total = 0;
  let streak = 0;
  const log: GradeFeedback[] = [];
  const keyMoments: GradeReport['keyMoments'] = [];
  const keyIndex = new Map<number, number>();

  // Toast pipeline.
  let lastToastT = -Infinity;
  const labelShownT = new Map<string, number>();
  let queue: Queued[] = [];
  let out: GradeFeedback[] = [];
  let now = 0;

  // Per-tick history.
  let prevPhase: MatchPhase | null = null;
  let prevFrames = new Map<string, CameraFrame>();
  let prevPgm: string | null = null;
  let cutCount = 0;
  let lastLiveCutT = -Infinity;
  let holdStartT = 0;
  let holdWasWide = true;
  const recentCoverage: { t: number; covered: boolean }[] = [];

  // Pacing state.
  let closeLostT = 0, lostFired = false;
  let stoppageStartT = 0, deadAirFired = false;
  let spellStartT = 0, spellCuts = 0;
  let lastReplayEndT = -Infinity;

  // Coverage aggregation window (periodic feedback instead of per-tick drains).
  let covWinT = 0, covWinW = 0, covWinCovered = 0, covWinIntensity = 0;

  // Pending judgements.
  let keyChecks: KeyCheck[] = [];
  let shotWindows: ShotWindow[] = [];
  let replayPending: ReplayPending[] = [];
  let gfxPending: GfxPending[] = [];
  const goalsAwaitingReplay = new Map<number, MatchEvent>();
  let restart: Restart | null = null;

  // Replay session.
  let replayWasActive = false;
  let replayStartT = 0;
  let replayCams = new Set<string>();
  let replayCreditedEv: MatchEvent | null = null;
  let replayTwoAngle = false;
  let replayLiveFlagged = false;

  // Graphics.
  let noBugT = 0, nextNoBugToast = 1.5;
  const seenGraphics = new Set<number>();
  const clutterFlagged = new Set<number>();
  const clutterT = new Map<number, number>();

  // Booth.
  const booth = new Map<number, BoothTrack>();

  // Viewer pulses this tick.
  let tickMissed = 0, tickCovered = 0;

  // ---- helpers -------------------------------------------------------------

  const clamp100 = (v: number) => Math.max(0, Math.min(100, v));

  /**
   * Discrete decision: move a category toward `target` by weight w (0..1). The first few
   * decisions in a category carry extra weight (evidence replaces the neutral start score
   * quickly), so the opening minutes don't flatter a director who isn't doing the job.
   */
  function nudge(cat: GradeCategory, target: number, w: number, evidence = true, reason = ''): void {
    if (evidence && opts.onDecision) opts.onDecision(cat, target, w, reason);
    const cur = scores[cat];
    let ww = w;
    if (evidence) {
      const n = decisions[cat]++;
      ww = Math.max(w, Math.min(0.5, w * 5) / (1 + n * 0.4));
    }
    ww = Math.min(1, target < cur ? ww * penaltyScale : ww);
    scores[cat] = clamp100(cur + (target - cur) * ww);
  }

  /** Continuous signal: drift toward target with time constant tau (seconds), rate-scaled. */
  function drift(cat: GradeCategory, target: number, dt: number, tau: number): void {
    if (dt <= 0) return;
    const k = 1 - Math.exp(-dt / tau);
    nudge(cat, target, k, false);
  }

  function push(category: GradeCategory, label: string, severity: GradeFeedback['severity'], o: PushOpts = {}): void {
    if (o.silent) return;
    const base = o.points ?? BASE_POINTS[severity];
    const mult = streakMultiplier(streak);
    const delta = Math.round(base > 0 ? base * mult : base * penaltyScale);
    total = Math.max(0, total + delta);
    if (o.streak !== false) {
      if (severity === 'good' || severity === 'great') streak++;
      else if (severity === 'bad' || severity === 'terrible') streak = 0;
    }
    const fb: GradeFeedback = { t: now, category, delta, label, severity };
    if (o.eventId !== undefined) fb.eventId = o.eventId;
    const big = o.big ?? (severity === 'great' || severity === 'terrible');
    if (big) emit(fb);
    else {
      // Merge repeats (e.g. a burst of JUMPY cuts) into the one queued toast.
      const dup = queue.find((q) => q.fb.label === label);
      if (dup) dup.fb.delta += delta;
      else queue.push({ fb, at: now });
    }
  }

  function emit(fb: GradeFeedback): void {
    fb.t = now; // toast time = when it is shown
    out.push(fb);
    log.push(fb);
    lastToastT = now;
    labelShownT.set(fb.label, now);
  }

  const SEV_RANK = { terrible: 4, great: 3, bad: 2, good: 1, info: 0 } as const;
  function flushQueue(): void {
    // Repeats of a label shown moments ago are dropped (their points already counted).
    queue = queue.filter((q) => now - q.at <= 2.5 && now - (labelShownT.get(q.fb.label) ?? -Infinity) >= TOAST_REPEAT_S);
    if (queue.length === 0 || now - lastToastT < 1) return;
    let bi = 0;
    for (let i = 1; i < queue.length; i++) {
      if (SEV_RANK[queue[i]!.fb.severity] > SEV_RANK[queue[bi]!.fb.severity]) bi = i;
    }
    const [q] = queue.splice(bi, 1);
    emit(q!.fb);
  }

  function frameShows(f: CameraFrame | null | undefined, subject: string | 'ball' | null): boolean {
    if (!f) return false;
    if (subject === null) return f.framing.focusInFrame;
    if (subject === 'ball') return f.framing.ballInFrame;
    return f.framing.subjectId === subject || f.framing.visible.includes(subject);
  }

  function markKey(evId: number, patch: Partial<{ covered: boolean; replayed: boolean }>): void {
    const i = keyIndex.get(evId);
    if (i === undefined) return;
    Object.assign(keyMoments[i]!, patch);
  }

  // ---- event intake --------------------------------------------------------

  function intake(ev: MatchEvent): void {
    if (ev.importance >= KEY_IMPORTANCE && !NOT_KEY.has(ev.type)) {
      keyIndex.set(ev.id, keyMoments.length);
      keyMoments.push({ eventId: ev.id, covered: false, replayed: false, label: keyLabel(ev) });
      // Pre-window: anything on PGM in the half-second before counts.
      const pre = recentCoverage.some((c) => c.t >= ev.t - 0.5 && c.covered);
      keyChecks.push({ ev, deadline: ev.t + Math.max(0.5, grace + 0.25), covered: pre });
    }
    const ideal = ev.ideal;
    if (ideal.windowSeconds > 0 && ideal.shotSizes.length > 0 && ev.importance >= 0.15) {
      const wantsTight = !ideal.shotSizes.some(isWideSize);
      shotWindows.push({
        ev, start: ev.t + grace, end: ev.t + Math.max(ideal.windowSeconds, grace + 0.5),
        judged: 0, matched: 0, matchRun: 0, wantsTight, greatCutGiven: false, optional: OPTIONAL_REACTIONS.has(ev.type),
      });
    }
    if (ideal.replayWorthy) replayPending.push({ ev, stoppageTime: 0, replayed: false, resolved: false });
    if (ev.type === 'goal') goalsAwaitingReplay.set(ev.id, ev);
    for (const kind of defaultGraphicsFor(ev)) {
      gfxPending.push({ ev, kind, deadline: ev.t + (GFX_DEADLINES[kind] ?? 15) });
    }
  }

  // ---- per-tick sections ---------------------------------------------------

  function phaseTransitions(s: WorldSnapshot, pgm: CameraFrame | null, inReplay: boolean): void {
    const ph = s.phase;
    if (prevPhase !== null && ph !== prevPhase) {
      if (DEAD_AIR_PHASES.has(ph) && !DEAD_AIR_PHASES.has(prevPhase)) {
        stoppageStartT = s.t; deadAirFired = false;
      }
      // Dead-ball spells (stoppage → set piece / celebration → kickoff): did the director keep a
      // longer one alive with a reaction or replay, or sit on one shot?
      if (prevPhase === 'open_play') { spellStartT = s.t; spellCuts = 0; }
      if (ph === 'open_play' && s.t - spellStartT >= 6 && spellStartT > 0) {
        nudge('pacing', spellCuts > 0 ? 95 : 40, 0.08);
      }
      // Ball back in play: the director should be on a covering wide shot.
      if (ph === 'open_play' && prevPhase !== 'full_time') {
        const wide = !inReplay && !!pgm && isWideSize(pgm.framing.shotSize);
        restart = { t0: s.t, deadline: s.t + 1.5 + grace, wasOffWide: !wide, held: 0 };
      }
      // Goals are judged once the restart kickoff is actually taken (the walk back is replay time).
      if (ph === 'open_play' && prevPhase === 'kickoff') {
        for (const [id, goal] of goalsAwaitingReplay) {
          if (goal.t < s.t - 1) {
            goalsAwaitingReplay.delete(id);
            push('replays', 'GOAL NOT REPLAYED', 'bad', { eventId: id, points: -150, big: true });
            nudge('replays', 0, 0.35);
          }
        }
      }
    }
    prevPhase = ph;
  }

  function processCuts(d: DirectorState, s: WorldSnapshot, live: boolean): void {
    const fresh = d.cuts.slice(cutCount);
    cutCount = d.cuts.length;
    const liveCuts = fresh.filter((c) => !c.isReplay);
    // Fallback when the mixer changed PGM without logging a cut.
    if (liveCuts.length === 0 && prevPgm !== null && d.programCamId !== prevPgm && !d.replay.active) {
      liveCuts.push({ t: s.t, camId: d.programCamId, transition: 'cut', isReplay: false });
    }
    let from = prevPgm;
    for (const cut of liveCuts) {
      const to = cut.camId;
      const isStinger = cut.transition === 'stinger';
      if (to === from && !isStinger) continue;
      const interval = cut.t - lastLiveCutT;
      const toKind = camKindOf(to);
      if (!isStinger) {
        // Pacing.
        if (interval < 1.5) {
          push('pacing', 'JUMPY', 'bad', { points: -30 });
          nudge('pacing', 20, 0.12);
        } else if (live && holdWasWide && interval >= 6 && interval <= 25) {
          nudge('pacing', 95, 0.1);
        } else if (interval >= 1.5) {
          nudge('pacing', 80, 0.04);
        }
        // Continuity.
        if (toKind === 'reverse_angle' && live) {
          push('continuity', 'CROSSED THE LINE', 'bad', { points: -80 });
          nudge('continuity', 10, 0.3);
        } else if (from !== null && isJumpCut(prevFrames.get(from), prevFrames.get(to))) {
          push('continuity', 'JUMP CUT', 'bad');
          nudge('continuity', 25, 0.2);
        } else if (interval >= 1.5) {
          nudge('continuity', 98, 0.025);
        }
        lastLiveCutT = cut.t;
      }
      holdStartT = cut.t;
      if (s.phase !== 'open_play') spellCuts++;
      closeLostT = 0; lostFired = false; deadAirFired = false;
      from = to;
    }
    prevPgm = d.programCamId;
  }

  function isJumpCut(a: CameraFrame | undefined, b: CameraFrame | undefined): boolean {
    if (!a || !b) return false;
    if (a.framing.shotSize !== b.framing.shotSize) return false;
    const dp = Math.hypot(a.position.x - b.position.x, a.position.y - b.position.y, a.position.z - b.position.z);
    if (dp > 12) return false;
    const da = dir(a), db = dir(b);
    const dot = da.x * db.x + da.y * db.y + da.z * db.z;
    return dot > Math.cos((20 * Math.PI) / 180) && Math.abs(a.fov - b.fov) / Math.max(a.fov, b.fov) < 0.35;
  }
  function dir(f: CameraFrame) {
    const x = f.target.x - f.position.x, y = f.target.y - f.position.y, z = f.target.z - f.position.z;
    const l = Math.hypot(x, y, z) || 1;
    return { x: x / l, y: y / l, z: z / l };
  }

  function coverage(dt: number, s: WorldSnapshot, pgm: CameraFrame | null, live: boolean, inReplay: boolean): void {
    const covered = !inReplay && !!pgm && (pgm.framing.focusInFrame || pgm.framing.ballInFrame);
    recentCoverage.push({ t: s.t, covered });
    while (recentCoverage.length > 0 && recentCoverage[0]!.t < s.t - 1) recentCoverage.shift();
    if (!live) return;
    const dangerous = s.intensity > 0.5;
    if (inReplay && !dangerous) return; // calm live play during a replay: the risk didn't bite
    const w = 0.35 + s.intensity;
    // Asymmetric: losing the action drains faster than holding it rebuilds. The drain is
    // weighted hard by intensity — a brief cutaway while the ball is being knocked about at
    // the back barely matters; leaving an attack costs a lot.
    const drainW = 0.08 + 1.1 * s.intensity ** 1.5;
    drift('coverage', covered ? 100 : 0, dt * (covered ? w : drainW), covered ? 20 : 7);
    if (covered) total += dt * (1 + 3 * s.intensity) * streakMultiplier(streak);
    covWinT += dt; covWinW += dt * w; covWinIntensity += dt * s.intensity;
    if (covered) covWinCovered += dt * w;
    if (covWinT >= 6) {
      const ratio = covWinCovered / Math.max(1e-6, covWinW);
      const avgI = covWinIntensity / covWinT;
      if (ratio < 0.5) {
        push('coverage', inReplay ? 'MISSED LIVE ACTION' : 'OFF THE ACTION', 'bad', { points: -40 });
      } else if (ratio > 0.97 && avgI > 0.5) {
        push('coverage', 'LOCKED ON', 'info', { points: 25, streak: false });
      }
      covWinT = 0; covWinW = 0; covWinCovered = 0; covWinIntensity = 0;
    }
  }

  function judgeKeyMoments(s: WorldSnapshot, pgm: CameraFrame | null, inReplay: boolean): void {
    const remaining: KeyCheck[] = [];
    for (const k of keyChecks) {
      if (!k.covered && s.t <= k.deadline + 1e-9) {
        if (!inReplay && pgm && !NON_GAME_KINDS.has(camKindOf(pgm.camId) ?? 'main_wide')
          && (pgm.framing.focusInFrame || pgm.framing.ballInFrame || frameShows(pgm, k.ev.ideal.subjectId)
            || (k.ev.playerId !== null && frameShows(pgm, k.ev.playerId)))) {
          k.covered = true;
        }
      }
      if (s.t < k.deadline) { remaining.push(k); continue; }
      const ev = k.ev;
      if (k.covered) {
        markKey(ev.id, { covered: true });
        tickCovered++;
        if (ev.type === 'goal') {
          push('coverage', 'ON THE GOAL!', 'great', { eventId: ev.id, points: 250 });
          nudge('coverage', 100, 0.2);
        } else {
          push('coverage', 'CAUGHT IT', 'good', { eventId: ev.id, points: Math.round(60 + 80 * ev.importance) });
          nudge('coverage', 100, 0.08 + 0.08 * ev.importance);
        }
      } else {
        tickMissed++;
        push('coverage', 'MISSED IT!', 'terrible', { eventId: ev.id, points: Math.round(-300 * ev.importance) });
        nudge('coverage', 0, ev.type === 'goal' ? 0.5 : 0.15 + 0.25 * ev.importance);
      }
    }
    keyChecks = remaining;
  }

  function shotChoice(dt: number, s: WorldSnapshot, pgm: CameraFrame | null, inReplay: boolean): void {
    const remaining: ShotWindow[] = [];
    for (const w of shotWindows) {
      if (s.t >= w.start && s.t <= w.end && !inReplay) {
        const match = !!pgm && matchesIdeal(pgm, w.ev);
        // A reaction close-up is never owed while the ball is in play (or the teams are lining up
        // for the kickoff) — a covering wide is right then.
        if (match || !w.wantsTight || (s.phase !== 'open_play' && s.phase !== 'kickoff')) w.judged += dt;
        w.matchRun = match ? w.matchRun + dt : 0;
        if (match) {
          w.matched += dt;
          // A cut made in response to the event that lands the ideal shot — and holds it.
          if (!w.greatCutGiven && w.wantsTight && lastLiveCutT >= w.ev.t - 0.25 && w.matchRun >= GREAT_CUT_HOLD_S) {
            w.greatCutGiven = true;
            const great = w.ev.importance >= KEY_IMPORTANCE;
            push('shot_choice', 'GREAT CUT', great ? 'great' : 'good', { eventId: w.ev.id, big: false });
            nudge('shot_choice', 100, 0.12, true, `great cut ${w.ev.type}`);
          }
        }
      }
      if (s.t <= w.end) { remaining.push(w); continue; }
      if (w.judged < 0.3) continue; // spent in replay — no judgement
      const f = w.matched / w.judged;
      const imp = w.ev.importance;
      if (w.optional) {
        if (f >= 0.4) nudge('shot_choice', 95, 0.03 + 0.1 * imp, true, `optional ${w.ev.type}`);
      } else if (w.wantsTight) {
        nudge('shot_choice', 20 + 80 * f, 0.05 + 0.2 * imp, true, `tight ${w.ev.type}`);
        if (f < 0.2 && imp >= 0.3 && !w.greatCutGiven) {
          push('shot_choice', 'NO REACTION', 'bad', { eventId: w.ev.id, points: -40 });
        }
      } else if (f >= 0.5) {
        // Wide-acceptable windows only ever reward (holding MAIN WIDE is the right default) — but
        // following play is coverage's job, so they only nudge shot choice gently.
        nudge('shot_choice', 90, 0.01 + 0.05 * imp, true, 'wide ok');
      } else if (imp >= 0.4) {
        nudge('shot_choice', 45, 0.05 * imp, true, `wide miss ${w.ev.type}`);
      }
    }
    shotWindows = remaining;
  }

  function matchesIdeal(f: CameraFrame, ev: MatchEvent): boolean {
    const ideal = ev.ideal;
    if (!ideal.shotSizes.includes(f.framing.shotSize)) return false;
    const kind = camKindOf(f.camId);
    if (ev.type === 'manager_reaction' && kind === 'bench') return true;
    if (ev.type === 'crowd_moment' && kind === 'crowd') return true;
    // Goal celebrations: the dugout going wild or the fans are reaction shots too, not just the scorer.
    if ((ev.type === 'goal' || ev.type === 'celebration') && (kind === 'bench' || kind === 'crowd')) return true;
    if (ideal.subjectId === null) return REACTION_TYPES.has(ev.type) ? true : f.framing.focusInFrame;
    return frameShows(f, ideal.subjectId);
  }

  function pacing(dt: number, s: WorldSnapshot, pgm: CameraFrame | null, live: boolean, inReplay: boolean): void {
    // LOST THE BALL: hanging on a close-up while play has moved on.
    if (live && !inReplay && pgm && isCloseSize(pgm.framing.shotSize) && !pgm.framing.focusInFrame && !pgm.framing.ballInFrame) {
      closeLostT += dt;
      if (closeLostT > 6 + grace) {
        if (!lostFired) {
          lostFired = true;
          push('pacing', 'LOST THE BALL', 'bad', { points: -60 });
          nudge('pacing', 25, 0.15);
        } else drift('pacing', 10, dt, 10);
      }
    } else { closeLostT = 0; lostFired = false; }

    // DEAD AIR: static stoppage > 20s.
    if (DEAD_AIR_PHASES.has(s.phase) && !inReplay) {
      const since = Math.max(lastLiveCutT, stoppageStartT, lastReplayEndT, holdStartT);
      if (s.t - since > 20) {
        if (!deadAirFired) {
          deadAirFired = true;
          push('pacing', 'DEAD AIR', 'bad', { points: -60 });
          nudge('pacing', 25, 0.2);
        } else drift('pacing', 15, dt, 12);
      }
    }
    holdWasWide = !!pgm && isWideSize(pgm.framing.shotSize);
  }

  function restartCheck(dt: number, s: WorldSnapshot, pgm: CameraFrame | null, inReplay: boolean): void {
    if (!restart) return;
    const kind = pgm ? camKindOf(pgm.camId) : null;
    const wide = !inReplay && !!pgm && isWideSize(pgm.framing.shotSize) && kind !== 'reverse_angle' && kind !== 'crowd';
    // The covering wide has to be held, not flashed through.
    restart.held = wide ? restart.held + dt : 0;
    if (restart.held >= RESTART_HOLD_S) {
      if (restart.wasOffWide) {
        push('shot_choice', 'BACK TO WIDE', 'good', { points: 50 });
        nudge('shot_choice', 95, 0.1);
        nudge('pacing', 90, 0.05);
      } else nudge('shot_choice', 85, 0.03);
      restart = null;
    } else if (s.t > restart.deadline && restart.held === 0) {
      push('shot_choice', 'LATE TO RESTART', 'bad', { points: -60 });
      nudge('shot_choice', 30, 0.15);
      restart = null;
    }
  }

  function replays(dt: number, s: WorldSnapshot, d: DirectorState, live: boolean): void {
    const r = d.replay;
    // Dead ball (stoppage, celebration, walk back for kickoff, set piece being lined up).
    const stoppageLike = !live && s.phase !== 'open_play';
    if (stoppageLike) for (const p of replayPending) if (!p.replayed) p.stoppageTime += dt;

    if (r.active && !replayWasActive) {
      replayStartT = s.t;
      if (s.phase !== 'open_play') spellCuts++;
      replayCams = new Set([r.camId]);
      replayCreditedEv = null;
      replayTwoAngle = false;
      replayLiveFlagged = false;
    }
    // Still in a replay once the ball is back in play and it's dangerous.
    if (r.active && !replayLiveFlagged && s.phase === 'open_play' && s.intensity > DANGER) {
      replayLiveFlagged = true;
      push('replays', 'MISSED LIVE ACTION', 'bad', { points: -80, big: true });
      nudge('replays', 20, 0.2);
    }
    if (r.active) {
      replayCams.add(r.camId);
      if (!replayCreditedEv) {
        const covered = replayPending.filter((p) =>
          (p.ev.t >= r.fromT - 0.5 && p.ev.t <= r.toT + 0.5) || p.ev.id === r.eventId);
        if (covered.length > 0) {
          covered.sort((a, b) => b.ev.importance - a.ev.importance);
          const best = covered[0]!;
          replayCreditedEv = best.ev;
          for (const p of covered) {
            p.replayed = true;
            goalsAwaitingReplay.delete(p.ev.id);
            markKey(p.ev.id, { replayed: true });
          }
          const age = replayStartT - best.ev.t;
          const startedLive = s.phase === 'open_play';
          if (age > REPLAY_WINDOW_S) {
            push('replays', 'LATE REPLAY', 'info', { eventId: best.ev.id, points: 20 });
            nudge('replays', 60, 0.1);
          } else if (startedLive && s.intensity > 0.5) {
            // Already penalised as MISSED LIVE ACTION; still counts as replayed.
          } else {
            const great = best.ev.type === 'goal';
            push('replays', 'REPLAY ✓', great ? 'great' : 'good', { eventId: best.ev.id, points: great ? 150 : 80, big: great });
            nudge('replays', startedLive ? 85 : 100, 0.15 + 0.25 * best.ev.importance);
          }
        }
      }
      if (replayCreditedEv && !replayTwoAngle && replayCams.size >= 2) {
        replayTwoAngle = true;
        push('replays', '2-ANGLE REPLAY', 'great', { eventId: replayCreditedEv.id, points: 120, big: false });
        nudge('replays', 100, 0.2);
      }
    }
    if (!r.active && replayWasActive) {
      lastReplayEndT = s.t;
      if (!replayCreditedEv) nudge('replays', 55, 0.05);
    }
    replayWasActive = r.active;

    // Expiry of replay-worthy moments (goals are judged at the next kickoff instead).
    for (const p of replayPending) {
      if (p.replayed || p.resolved || s.t - p.ev.t <= REPLAY_WINDOW_S) continue;
      p.resolved = true;
      if (p.ev.type === 'goal') continue;
      if (p.stoppageTime < 4) continue; // never had a chance — no penalty
      if (p.ev.importance >= KEY_IMPORTANCE) {
        push('replays', 'NO REPLAY', 'bad', { eventId: p.ev.id, points: -50 });
        nudge('replays', 15, 0.15);
      } else {
        nudge('replays', 40, 0.04 + 0.1 * p.ev.importance);
      }
    }
    replayPending = replayPending.filter((p) => s.t - p.ev.t <= 120);
  }

  function graphics(dt: number, s: WorldSnapshot, d: DirectorState, live: boolean, inReplay: boolean): void {
    const onAir = d.graphics.filter((g) => g.hiddenAt === undefined);
    // Scorebug during open play.
    if (s.phase === 'open_play' && !inReplay) {
      if (onAir.some((g) => g.kind === 'scorebug')) {
        noBugT = 0; nextNoBugToast = 1.5;
        // The scorebug is hygiene, not craft: it holds the score ~75; event graphics earn the rest.
        drift('graphics', 75, dt, 240);
      } else {
        noBugT += dt;
        drift('graphics', 0, dt, 30);
        if (noBugT >= nextNoBugToast) {
          push('graphics', 'NO SCOREBUG', 'bad', { points: -30, streak: nextNoBugToast < 3 });
          nextNoBugToast += 10;
        }
      }
    }
    // Newly shown graphics satisfy pending requirements.
    const all = d.graphicsLog.length > 0 ? d.graphicsLog : d.graphics;
    for (const g of [...all, ...d.graphics]) {
      if (seenGraphics.has(g.id)) continue;
      seenGraphics.add(g.id);
      const idx = gfxPending.findIndex((p) => p.kind === g.kind && g.shownAt >= p.ev.t - 1
        && (!g.subjectId || !p.ev.playerId || g.subjectId === p.ev.playerId));
      if (idx >= 0) {
        const p = gfxPending.splice(idx, 1)[0]!;
        push('graphics', GFX_OK_LABEL[p.kind] ?? 'GFX ✓', 'good', { eventId: p.ev.id, points: 70 });
        nudge('graphics', 100, 0.2);
      }
    }
    gfxPending = gfxPending.filter((p) => {
      if (s.t <= p.deadline) return true;
      push('graphics', GFX_MISS_LABEL[p.kind] ?? 'NO GFX', 'bad', { eventId: p.ev.id, points: -40 });
      nudge('graphics', 20, 0.2);
      return false;
    });
    // CLUTTER: a non-scorebug graphic on screen during high intensity live play.
    // (Reaction grace: a graphic that was up before the attack built gets a moment to be cleared.)
    if (live && !inReplay && s.intensity > 0.6) {
      for (const g of onAir) {
        if (PASSIVE_GRAPHICS.has(g.kind) || clutterFlagged.has(g.id)) continue;
        const tOn = (clutterT.get(g.id) ?? 0) + dt;
        clutterT.set(g.id, tOn);
        if (tOn < 0.75 + grace && s.t - g.shownAt > tOn + 0.05) continue;
        clutterFlagged.add(g.id);
        push('graphics', 'CLUTTER', 'bad', { points: -50 });
        nudge('graphics', 20, 0.2);
      }
    }
  }

  function boothCheck(s: WorldSnapshot, d: DirectorState, pgm: CameraFrame | null, inReplay: boolean, requests: CommentatorRequest[]): void {
    for (const req of requests) {
      if (!booth.has(req.id)) booth.set(req.id, { req, lateUntil: req.expiresAt + BOOTH_LATE_WINDOW_S });
    }
    for (const b of booth.values()) {
      if (b.status) continue;
      const req = b.req;
      if (s.t < req.t) continue;
      const w = req.wants;
      let ok = true;
      if (w.graphic) {
        ok &&= d.graphics.some((g) => g.hiddenAt === undefined && g.kind === w.graphic
          && (!w.subjectId || g.subjectId === w.subjectId));
      }
      if (w.camKind) ok &&= !inReplay && !!pgm && camKindOf(pgm.camId) === w.camKind;
      if (w.replay) ok &&= d.replay.active;
      if (!w.graphic && !w.camKind && !w.replay) ok = false;
      let status: BoothStatus | undefined;
      if (ok) status = s.t <= req.expiresAt ? 'hit' : 'late';
      else if (s.t > b.lateUntil) status = 'missed';
      if (!status) continue;
      b.status = status;
      if (status === 'hit') {
        push('booth', 'BOOTH ✓', 'good', { points: 100 });
        nudge('booth', 100, 0.3);
      } else if (status === 'late') {
        push('booth', 'BOOTH LATE', 'info', { points: 40, streak: false });
        nudge('booth', 60, 0.2);
      } else {
        push('booth', 'BOOTH MISSED', 'bad', { points: -60 });
        nudge('booth', 0, 0.3);
      }
      opts.onBoothResolved?.(req.id, status);
    }
  }

  // ---- public API ----------------------------------------------------------

  return {
    tick(input: GradeTickInput): GradeFeedback[] {
      const { dt, snapshot: s, director: d } = input;
      now = s.t;
      out = [];
      tickMissed = 0; tickCovered = 0;
      const inReplay = d.replay.active;
      const frames = new Map(input.allFrames.map((f) => [f.camId, f] as const));
      const pgm = inReplay ? null : (input.programFrame ?? frames.get(d.programCamId) ?? null);
      const live = isLive(s);

      phaseTransitions(s, pgm, inReplay);
      processCuts(d, s, live);
      for (const ev of input.newEvents) intake(ev);
      coverage(dt, s, pgm, live, inReplay);
      judgeKeyMoments(s, pgm, inReplay);
      shotChoice(dt, s, pgm, inReplay);
      pacing(dt, s, pgm, live, inReplay);
      restartCheck(dt, s, pgm, inReplay);
      replays(dt, s, d, live);
      graphics(dt, s, d, live, inReplay);
      boothCheck(s, d, pgm, inReplay, input.requests);

      const rating = applyDifficulty(compositeRating(scores), difficulty);
      viewers.update(dt, { rating, bigCovered: tickCovered, missed: tickMissed, streak, intensity: live ? s.intensity : 0 });
      flushQueue();
      prevFrames = frames;
      return out;
    },

    report(): GradeReport {
      const byCategory = { ...scores };
      for (const k of Object.keys(byCategory) as GradeCategory[]) byCategory[k] = Math.round(byCategory[k] * 10) / 10;
      const rating = Math.round(applyDifficulty(compositeRating(scores), difficulty) * 10) / 10;
      return {
        total: Math.round(total),
        byCategory,
        rating,
        letter: letterFor(rating),
        viewers: viewers.viewers(),
        streak,
        feedback: log.slice(),
        keyMoments: keyMoments.map((k) => ({ ...k })),
      };
    },

    multiplier: () => streakMultiplier(streak),
    requestStatus: (id) => booth.get(id)?.status,
    peakViewers: () => viewers.peak(),
  };
}
